import { ApolloError } from '@apollo/client';
import { noop } from 'lodash';
import { createContext, Dispatch, SetStateAction } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import {
  EstatePage_EntityFragment,
  EstatePage_TestamentaryEntityFragment,
  EstatePage_UnacknowledgedAiSuggestionFragment,
  EstatePage_UnacknowledgedHoldingSuggestionFragment,
} from '@/modules/estate/graphql/EstatePageData.generated';

export type EstatePageSuggestionSummary =
  | EstatePage_UnacknowledgedAiSuggestionFragment
  | EstatePage_UnacknowledgedHoldingSuggestionFragment;

export interface EstateHoldingsContext {
  loading: boolean;
  setLoading: Dispatch<SetStateAction<EstateHoldingsContext['loading']>>;

  entities: EstatePage_EntityFragment[] | null;
  setEntities: Dispatch<SetStateAction<EstateHoldingsContext['entities']>>;

  testamentaryEntities: EstatePage_TestamentaryEntityFragment[] | null;
  setTestamentaryEntities: Dispatch<
    SetStateAction<EstateHoldingsContext['testamentaryEntities']>
  >;

  draftEntities: EstatePage_EntityFragment[] | null;
  setDraftEntities: Dispatch<
    SetStateAction<EstateHoldingsContext['draftEntities']>
  >;

  // V2: Map of entity/testamentary entity ID to their unacknowledged suggestions
  unacknowledgedSuggestionsV2: Record<string, EstatePageSuggestionSummary[]>;
  setUnacknowledgedSuggestionsV2: Dispatch<
    SetStateAction<EstateHoldingsContext['unacknowledgedSuggestionsV2']>
  >;

  error: ApolloError | null;
  setError: Dispatch<SetStateAction<EstateHoldingsContext['error']>>;

  refetch: (() => void) | null;
  setRefetch: Dispatch<SetStateAction<EstateHoldingsContext['refetch']>>;
}

export const EstateHoldingsContext = createContext<EstateHoldingsContext>({
  loading: false,
  setLoading: noop,

  entities: null,
  setEntities: () => null,

  testamentaryEntities: null,
  setTestamentaryEntities: () => null,

  draftEntities: null,
  setDraftEntities: () => null,

  unacknowledgedSuggestionsV2: {},
  setUnacknowledgedSuggestionsV2: noop,

  error: null,
  setError: () => null,

  refetch: null,
  setRefetch: () => null,
});

export const useEstateHoldingsContext = () => {
  return useGuardedContext(EstateHoldingsContext, 'EstateHoldingsProvider');
};
